import { SupportedAccent } from "../../ruleSets/allRules";
import { SpanishAccentType } from "../../ruleSets/spanishToIpa";

/** different types of inputs that are accepted */
export type InputType = 'IPA' | 'Americanist' | 'CXS' | 'English (US)' | 'English (UK)' | 'Old English' | 'Spanish' | 'Lushootseed';
export const inputOptions: (InputType | '-')[] = ['IPA', 'Americanist', 'CXS', '-', 'English (US)', 'English (UK)', 'Old English', '-', 'Spanish'];

/** Spanish accents */
export const spanishAccents: SpanishAccentType[] = ['Mexico', 'Spain', 'Americanized'];

/** default accent for a given input type */
export function getDefaultAccent(inputType: InputType): SupportedAccent {
  switch (inputType) {
    case 'English (US)':
      return 'Western US';
    case 'English (UK)': return 'English RP';
    case 'Spanish': return 'Mexico';
    default: return 'none';
  }
}

const sampleIPA = // You can enter text phonetically using IPA, the International Phonetic Alphabet, and listen to it spoken.
  'ˈju kʰæn ˈɛnɝ ˈtʰɛkst fəˈnɛɾɪkɫi ˈjuzɪŋ ˈaɪ̯ˈpʰiˈeɪ̯ | ðɨ ˌɪnɝˈnæʃn̩ɫ̩ fɨˈnɛɾɪk ˈæɫfɨˌbɛt | ænd ˈɫɪsn̩ tʰə ɪt ˈspoʊ̯kn̩ ‖';
const sampleAmericanist = // You can enter text phonetically using Americanist phonetic notation and it'll convert it to IPA.
  'yú kæn ɛ́nɜɹ tɛ́kst fənɛ́tɪkɫi yúzɪŋ əmɛ́ɹəkənɪst fənɛ́tɪk nótéʃən ænd ɪ́təɫ kənvɜ́ɹt ɪt tə áɪpíé ‖';
const sampleCXS = // You can enter text phonetically using CXS, Conlang X-SAMPA, and it'll convert it to IPA.
  "'ju k@n 'En3` 'tEkst f@'nE4Ikli 'juzIN 'si'Eks'Es | 'kAnl&N 'Eks 's&mp@ | &nd I4@l k@n'v3`t It t@ 'aI_^'pi'eI_^ ||";
const sampleEnglishUS = "Type English into the input box. It'll put the pronunciation from the US dictionary in the output box and the chosen accent in the accent box.";
const sampleEnglishUK = "Type English into the input box. It'll put the pronunciation from the UK dictionary in the output box and the chosen accent in the accent box.";
const sampleOldEnglish = 'Beowulf\nHwæt! We Gardena in geardagum, þeodcyninga, þrym gefrunon, hu ða æþelingas ellen fremedon.';
const sampleSpanish = // https://es.wikisource.org/wiki/El_ingenioso_hidalgo_Don_Quijote_de_la_Mancha_(1905)/Tomo_I/Cap%C3%ADtulo_I
  'El ingenioso hidalgo Don Quijote de la Mancha, parte primera.\n  En un lugar de la Mancha, de cuyo nombre no quiero acordarme, no ha mucho tiempo que vivía un hidalgo de los de lanza en astillero, adarga antigua, rocín flaco y galgo corredor.';
const sampleLushootseed = // the Universal Declaration of Human Rights
  'ʔəsdiɬdiɬgʷəs ti sdᶻəw̓il ʔi ti staltalx̌ ʔə ti sbək̓ʷaʔkʷbixʷ tul̕ʔal ti sgʷəcs. ʔəstalx̌ əlgʷəʔ kʷi gʷəsx̌əčbids gʷəl ɬutabab ti bək̓ʷaʔkʷbixʷ x̌əɬ ti tə təɬ syəyaʔyaʔ.';

/** map from input-type to sample-input */
export const sampleText: Record<InputType, string> = {
  'IPA': sampleIPA,
  'Americanist': sampleAmericanist,
  'CXS': sampleCXS,
  'English (US)': sampleEnglishUS,
  'English (UK)': sampleEnglishUK,
  'Old English': sampleOldEnglish,
  'Spanish': sampleSpanish,
  'Lushootseed': sampleLushootseed,
}
